/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { CSSTransition } from 'react-transition-group';

import styled from '../../styled';

export type MessageType = 'error' | 'warning' | 'message';

export interface MessageProps {
	type?: MessageType;
	message?: string;
	isActive?: boolean;
}

const Wrap = styled('div')`
	padding: 1ex 0;
	font-weight: ${props => props.theme.font.weight.regular};
	color: ${props => props.theme.color.grey};
	border-top: 1px solid ${props => props.theme.color.grey};
	border-bottom: 1px solid ${props => props.theme.color.grey};
	transition: all 300ms ease;
	overflow: hidden;
	opacity: 1;

	&.error {
		border-color: ${props => props.theme.color.error};
		color: ${props => props.theme.color.error};
	}

	&.warning {
		border-color: ${props => props.theme.color.warning};
		color: ${props => props.theme.color.warning};
	}

	&.message {
		border-color: ${props => props.theme.color.message};
		color: ${props => props.theme.color.message};
	}

	&.notifications-appear,
	&.notifications-enter {
		padding: 0;
		opacity: 0;
		border-width: 0;
	}

	&.notifications-exit-active {
		padding: 0;
		opacity: 0;
		border-width: 0;
	}
`;

export const Message: React.FunctionComponent<MessageProps> = ({
	isActive,
	type,
	message,
	...props
}) => {
	const onEnter = (node: HTMLElement) => {
		const rect = node.getBoundingClientRect();
		node.setAttribute('data-height', rect.height - node.clientHeight + 'px');
		node.style.height = '0px';
	};
	const onEntering = (node: HTMLElement) => {
		node.style.height = node.getAttribute('data-height')!;
	};
	const onEntered = (node: HTMLElement) => {
		node.style.height = '';
	};

	const onExit = (node: HTMLElement) => {
		const rect = node.getBoundingClientRect();
		node.style.height = rect.height - node.clientHeight + 'px';
	};
	const onExiting = (node: HTMLElement) => {
		node.style.height = '0px';
	};
	const onExited = (node: HTMLElement) => {
		node.style.height = '';
	};

	return (
		<CSSTransition
			classNames="notifications"
			in={isActive}
			appear
			mountOnEnter
			unmountOnExit
			timeout={300}
			onEnter={onEnter}
			onEntering={onEntering}
			onEntered={onEntered}
			onExit={onExit}
			onExiting={onExiting}
			onExited={onExited}
		>
			<Wrap className={type} {...props}>
				{message}
			</Wrap>
		</CSSTransition>
	);
};

export default Message;
